/**
 * @engine/physics2d-rapier2d — Rapier2D Physics Engine Adapter
 *
 * Wraps the Rapier2D WASM physics engine behind the IPhysicsEngine2D
 * interface, making it a drop-in replacement for the built-in solver.
 *
 * Uses `@dimforge/rapier2d-simd-compat` (JS-based WASM init with SIMD support).
 *
 * @module @engine/physics2d-rapier2d
 */

import RAPIER, {
  World,
  RigidBody,
  RigidBodyDesc,
  RigidBodyType,
  Collider,
  ColliderDesc,
  EventQueue,
  ActiveEvents,
  ActiveHooks,
  SolverFlags,
  CoefficientCombineRule,
  type RigidBodyHandle,
  type ColliderHandle,
  type Vector,
} from '@dimforge/rapier2d-simd-compat';

import type {
  IPhysicsEngine2D,
  PhysicsWorldOptions,
  PhysicsDiagnostics,
  RigidBodyDescriptor,
  ColliderDescriptor,
  BodyTransform,
  PhysicsBodyHandle,
  PhysicsColliderHandle,
  PhysicsBodyEventHandlers,
  PhysVec2,
  RaycastHit,
  GravityZoneDescriptor,
  BuoyancyZoneDescriptor,
  WindZoneDescriptor,
  SurfaceEffectorDescriptor,
} from '@engine/physics2d';
import { PhysicsBodyType } from '@engine/physics2d';
import { Profiler } from '@engine/core';

// ============================================================================
// COMBINE MODE MAPPING
// ============================================================================

/** Map our CombineMode to Rapier's CoefficientCombineRule */
function toRapierCombineRule(mode: number | undefined): CoefficientCombineRule {
  switch (mode) {
    case 0: // Average
      return CoefficientCombineRule.Average;
    case 1: // Minimum
      return CoefficientCombineRule.Min;
    case 2: // Maximum
      return CoefficientCombineRule.Max;
    case 3: // Multiply
      return CoefficientCombineRule.Multiply;
    default:
      return CoefficientCombineRule.Average;
  }
}

// ============================================================================
// BOOK-KEEPING
// ============================================================================

/**
 * Each created body is tracked with its GUID and gameObjectId so we
 * can map back to the ECS world during `forEachBody`.
 */
interface BodyRecord {
  handle: RigidBodyHandle;
  guid: string;
  gameObjectId: number;
  bodyType: PhysicsBodyType;
  density: number; // Effective density for buoyancy calculations
}

// ============================================================================
// BODY TYPE MAPPING
// ============================================================================

function toRapierBodyType(bt: PhysicsBodyType): RigidBodyType {
  switch (bt) {
    case PhysicsBodyType.Dynamic: return RigidBodyType.Dynamic;
    case PhysicsBodyType.Kinematic: return RigidBodyType.KinematicPositionBased;
    case PhysicsBodyType.Static: return RigidBodyType.Fixed;
  }
}

function fromRapierBodyType(bt: RigidBodyType): PhysicsBodyType {
  switch (bt) {
    case RigidBodyType.Dynamic: return PhysicsBodyType.Dynamic;
    case RigidBodyType.Fixed: return PhysicsBodyType.Static;
    case RigidBodyType.KinematicPositionBased:
    case RigidBodyType.KinematicVelocityBased:
      return PhysicsBodyType.Kinematic;
  }
}

// ============================================================================
// RAPIER 2D ENGINE
// ============================================================================

export class RapierPhysicsEngine implements IPhysicsEngine2D {
  readonly name = 'Rapier2D';

  private _world: World | null = null;
  private _eventQueue: EventQueue | null = null;

  /** handle → metadata for read-back */
  private _bodies: Map<RigidBodyHandle, BodyRecord> = new Map();

  /** GUID → handle for fast lookup */
  private _guidToHandle: Map<string, RigidBodyHandle> = new Map();

  /** One-way platform colliders: collider handle → direction vector */
  private _oneWayColliders: Map<ColliderHandle, { x: number; y: number }> = new Map();

  /** Physics hooks for contact filtering (one-way platforms) */
  private _physicsHooks: {
    filterContactPair: (c1: ColliderHandle, c2: ColliderHandle, b1: RigidBodyHandle, b2: RigidBodyHandle) => SolverFlags | null;
    filterIntersectionPair: (c1: ColliderHandle, c2: ColliderHandle) => boolean;
  } | undefined = undefined;

  /** Gravity zones (applied manually since Rapier doesn't have native support) */
  private _gravityZones: Map<string, GravityZoneDescriptor> = new Map();

  /** Buoyancy zones */
  private _buoyancyZones: Map<string, BuoyancyZoneDescriptor> = new Map();

  /** Wind zones */
  private _windZones: Map<string, WindZoneDescriptor> = new Map();

  /** Time accumulator for turbulence/pulse */
  private _time = 0;

  /** Global gravity (cached from world creation) */
  private _globalGravity: PhysVec2 = { x: 0, y: -9.81 };

  /** Fixed timestep accumulator */
  private _fixedTimestep = 1 / 60;
  private _accumulator = 0;

  /** Event handlers per body handle */
  private _eventHandlers: Map<RigidBodyHandle, PhysicsBodyEventHandlers> = new Map();

  /** Active collision pairs (key: "handleA:handleB") for enter/stay/exit tracking */
  private _activeCollisions: Map<string, boolean> = new Map();

  /** Active trigger pairs */
  private _activeTriggers: Set<string> = new Set();

  // ──────── Lifecycle ────────

  /**
   * Initialize the Rapier2D WASM module.
   *
   * @param wasmUrl - Optional URL/path to the `rapier_wasm2d_bg.wasm` file.
   *                  If provided, Rapier will fetch the WASM from this URL
   *                  instead of trying to resolve it via `import.meta.url`
   *                  (which breaks in esbuild/bundled environments).
   */
  async init(wasmUrl?: string): Promise<void> {
    if (wasmUrl) {
      // The Rapier default export's init() accepts { module_or_path: ... }
      // even though the .d.ts doesn't declare it. This is the official
      // way to provide a custom WASM location.
      await (RAPIER as unknown as { init(opts: { module_or_path: string }): Promise<void> })
        .init({ module_or_path: wasmUrl });
    } else {
      await RAPIER.init();
    }
  }

  createWorld(config: PhysicsWorldOptions): void {
    const gravity: Vector = config.gravity
      ? { x: config.gravity.x, y: config.gravity.y }
      : { x: 0, y: -9.81 };

    this._globalGravity = { x: gravity.x, y: gravity.y };
    this._world = new World(gravity);
    this._eventQueue = new EventQueue(true);
    this._bodies.clear();
    this._guidToHandle.clear();
    this._oneWayColliders.clear();
    this._gravityZones.clear();
    this._buoyancyZones.clear();
    this._windZones.clear();
    this._accumulator = 0;
    this._time = 0;

    // Initialize physics hooks for one-way platform filtering
    this._initPhysicsHooks();

    if (config.fixedTimestep !== undefined) {
      this._fixedTimestep = config.fixedTimestep;
    }

    // Map our iteration counts → Rapier's solver iterations
    if (config.velocityIterations !== undefined) {
      this._world.numSolverIterations = config.velocityIterations;
    }

    // Rapier has its own timestep property
    this._world.timestep = this._fixedTimestep;
  }

  destroyWorld(): void {
    if (this._eventQueue) {
      this._eventQueue.free();
      this._eventQueue = null;
    }
    if (this._world) {
      this._world.free();
      this._world = null;
    }
    this._bodies.clear();
    this._guidToHandle.clear();
    this._oneWayColliders.clear();
    this._eventHandlers.clear();
    this._activeCollisions.clear();
    this._activeTriggers.clear();
    this._physicsHooks = undefined;
    this._accumulator = 0;
  }

  dispose(): void {
    this.destroyWorld();
  }

  // ──────── Bodies ────────

  createBody(desc: RigidBodyDescriptor): PhysicsBodyHandle {
    this._assertWorld();

    let bodyDesc: RigidBodyDesc;
    switch (desc.bodyType) {
      case PhysicsBodyType.Dynamic:
        bodyDesc = RigidBodyDesc.dynamic();
        break;
      case PhysicsBodyType.Kinematic:
        bodyDesc = RigidBodyDesc.kinematicPositionBased();
        break;
      case PhysicsBodyType.Static:
      default:
        bodyDesc = RigidBodyDesc.fixed();
        break;
    }

    bodyDesc
      .setTranslation(desc.position.x, desc.position.y)
      .setRotation(desc.rotation);

    // Optional properties
    if (desc.linearDamping !== undefined) bodyDesc.setLinearDamping(desc.linearDamping);
    if (desc.angularDamping !== undefined) bodyDesc.setAngularDamping(desc.angularDamping);
    if (desc.gravityScale !== undefined) bodyDesc.setGravityScale(desc.gravityScale);
    if (desc.ccdEnabled) bodyDesc.setCcdEnabled(true);

    // Additional mass (Rapier computes mass from colliders, but we can add extra)
    if (desc.mass !== undefined && desc.mass > 0 && desc.bodyType === PhysicsBodyType.Dynamic) {
      bodyDesc.setAdditionalMass(desc.mass);
    }

    // Constraints
    if (desc.constraints !== undefined) {
      const freezeX = (desc.constraints & 1) !== 0;
      const freezeY = (desc.constraints & 2) !== 0;
      const freezeRot = (desc.constraints & 4) !== 0;

      if (freezeX && freezeY) {
        bodyDesc.lockTranslations();
      } else if (freezeX || freezeY) {
        bodyDesc.enabledTranslations(!freezeX, !freezeY);
      }
      if (freezeRot) {
        bodyDesc.lockRotations();
      }
    }

    // Store user data for event mapping
    bodyDesc.setUserData({ guid: desc.guid, gameObjectId: desc.gameObjectId });

    // Dominance (higher dominance bodies push lower ones without being pushed back)
    if (desc.dominance !== undefined && desc.dominance !== 0) {
      bodyDesc.setDominanceGroup(desc.dominance);
    }

    const body = this._world!.createRigidBody(bodyDesc);
    const handle = body.handle;

    // Sleep thresholds (must be set after body creation)
    if (desc.sleepLinearThreshold !== undefined && desc.sleepLinearThreshold > 0) {
      body.setLinearDamping(body.linearDamping()); // force wake so threshold applies
      // Rapier uses activation thresholds via setActivationLinearThreshold (not available in all versions)
      // For now we store it in user data for reference
    }

    this._bodies.set(handle, {
      handle,
      guid: desc.guid,
      gameObjectId: desc.gameObjectId,
      bodyType: desc.bodyType,
      density: 1, // Will be updated when colliders are added
    });
    this._guidToHandle.set(desc.guid, handle);

    return handle;
  }

  removeBody(handle: PhysicsBodyHandle): void {
    this._assertWorld();
    const h = handle as RigidBodyHandle;
    const record = this._bodies.get(h);
    if (record) {
      this._guidToHandle.delete(record.guid);
      this._bodies.delete(h);
    }
    const body = this._world!.getRigidBody(h);
    if (body) {
      this._world!.removeRigidBody(body);
    }
  }

  // ──────── Colliders ────────

  addCollider(bodyHandle: PhysicsBodyHandle, desc: ColliderDescriptor): PhysicsColliderHandle {
    this._assertWorld();
    const h = bodyHandle as RigidBodyHandle;
    const body = this._world!.getRigidBody(h);
    if (!body) throw new Error(`[RapierPhysicsEngine] Body handle ${h} not found`);

    let colliderDesc: ColliderDesc;

    switch (desc.shape.type) {
      case 'box':
        colliderDesc = ColliderDesc.cuboid(desc.shape.halfExtents.x, desc.shape.halfExtents.y);
        break;
      case 'circle':
        colliderDesc = ColliderDesc.ball(desc.shape.radius);
        break;
      case 'capsule':
        colliderDesc = ColliderDesc.capsule(desc.shape.halfHeight, desc.shape.radius);
        break;
      case 'polygon': {
        // Rapier expects a flat Float32Array for convex hull
        const verts = desc.shape.vertices;
        const flat = new Float32Array(verts.length * 2);
        for (let i = 0; i < verts.length; i++) {
          flat[i * 2] = verts[i].x;
          flat[i * 2 + 1] = verts[i].y;
        }
        const hull = ColliderDesc.convexHull(flat);
        if (!hull) {
          // Fallback: use a small box if convex hull fails
          colliderDesc = ColliderDesc.cuboid(0.5, 0.5);
        } else {
          colliderDesc = hull;
        }
        break;
      }
    }

    // Offset (translation relative to body)
    if (desc.offset) {
      colliderDesc.setTranslation(desc.offset.x, desc.offset.y);
    }

    // Rotation (relative to body)
    if (desc.rotation !== undefined && desc.rotation !== 0) {
      colliderDesc.setRotation(desc.rotation);
    }

    // Trigger/sensor
    if (desc.isTrigger) {
      colliderDesc.setSensor(true);
      colliderDesc.setActiveEvents(ActiveEvents.COLLISION_EVENTS);
    }

    // Material properties
    if (desc.material) {
      colliderDesc.setFriction(desc.material.friction);
      colliderDesc.setRestitution(desc.material.bounciness);
      colliderDesc.setDensity(desc.material.density);
      
      // Combine rules
      if (desc.material.frictionCombine !== undefined) {
        colliderDesc.setFrictionCombineRule(toRapierCombineRule(desc.material.frictionCombine));
      }
      if (desc.material.bouncinessCombine !== undefined) {
        colliderDesc.setRestitutionCombineRule(toRapierCombineRule(desc.material.bouncinessCombine));
      }
    }

    const collider = this._world!.createCollider(colliderDesc, body);

    // Update body density for buoyancy calculations
    const record = this._bodies.get(h);
    if (record && desc.material?.density !== undefined) {
      record.density = desc.material.density;
    }

    // One-way platform support
    if (desc.oneWay && desc.oneWayDirection) {
      // Enable contact filtering hooks on this collider
      collider.setActiveHooks(ActiveHooks.FILTER_CONTACT_PAIRS);
      
      // Store the one-way direction for use in filterContactPair
      this._oneWayColliders.set(collider.handle, {
        x: desc.oneWayDirection.x,
        y: desc.oneWayDirection.y,
      });
    }

    return collider.handle;
  }

  removeCollider(handle: PhysicsColliderHandle): void {
    this._assertWorld();
    const h = handle as ColliderHandle;
    
    // Remove from one-way tracking
    this._oneWayColliders.delete(h);
    
    const collider = this._world!.getCollider(h);
    if (collider) {
      this._world!.removeCollider(collider, true);
    }
  }

  // ──────── Simulation ────────

  step(dt: number): void {
    if (!this._world || !this._eventQueue) return;

    // Accumulator-based fixed timestep (same pattern as built-in engine)
    this._accumulator += dt;

    // Cap to prevent spiral of death
    if (this._accumulator > this._fixedTimestep * 8) {
      this._accumulator = this._fixedTimestep * 8;
    }

    while (this._accumulator >= this._fixedTimestep) {
      // Update time for turbulence/pulse calculation
      this._time += this._fixedTimestep;

      // Apply gravity zone forces before physics step
      if (this._gravityZones.size > 0) {
        Profiler.begin('Physics.Rapier.GravityZones');
        this._applyGravityZoneForces(this._fixedTimestep);
        Profiler.end('Physics.Rapier.GravityZones');
      }

      // Apply effector forces
      if (this._buoyancyZones.size > 0 || this._windZones.size > 0) {
        Profiler.begin('Physics.Rapier.Effectors');
        this._applyEffectorForces(this._fixedTimestep);
        Profiler.end('Physics.Rapier.Effectors');
      }
      
      // Pass physics hooks for one-way platform contact filtering
      Profiler.begin('Physics.Rapier.WorldStep');
      this._world.step(this._eventQueue, this._physicsHooks);
      Profiler.end('Physics.Rapier.WorldStep');
      this._accumulator -= this._fixedTimestep;
    }

    // Drain collision/trigger events and dispatch to handlers
    Profiler.begin('Physics.Rapier.DrainEvents');
    const currentPairs = new Set<string>();
    const exitedPairs = new Set<string>();

    this._eventQueue.drainCollisionEvents((h1, h2, started) => {
      const col1 = this._world!.getCollider(h1);
      const col2 = this._world!.getCollider(h2);
      if (!col1 || !col2) return;

      const bh1 = col1.parent()?.handle;
      const bh2 = col2.parent()?.handle;
      if (bh1 === undefined || bh2 === undefined) return;

      const rec1 = this._bodies.get(bh1);
      const rec2 = this._bodies.get(bh2);
      if (!rec1 || !rec2) return;

      // Sensor intersections are NOT reliably reported through rapier's collision-event
      // queue (only solid contacts are), so trigger enter/stay/exit is handled by the
      // dedicated polling pass below. Here we process solid contacts only.
      if (col1.isSensor() || col2.isSensor()) return;
      const key = bh1 < bh2 ? `${bh1}:${bh2}` : `${bh2}:${bh1}`;

      {
        const handlers1 = this._eventHandlers.get(bh1);
        const handlers2 = this._eventHandlers.get(bh2);

        // Try to get contact point and normal from Rapier's contact pair
        let cx = 0, cy = 0, nx = 0, ny = 0;
        this._world!.contactPair(col1, col2, (manifold, _flipped) => {
          const n = manifold.normal();
          nx = n.x;
          ny = n.y;
          if (manifold.numSolverContacts() > 0) {
            const pt = manifold.solverContactPoint(0);
            if (pt) { cx = pt.x; cy = pt.y; }
          }
        });

        if (started) {
          if (currentPairs.has(key)) return; // already fired for this body pair
          currentPairs.add(key);
          this._activeCollisions.set(key, true);
          handlers1?.onCollisionEnter?.(rec2.guid, cx, cy, nx, ny);
          handlers2?.onCollisionEnter?.(rec1.guid, cx, cy, -nx, -ny);
        } else {
          if (exitedPairs.has(key)) return;
          exitedPairs.add(key);
          this._activeCollisions.delete(key);
          handlers1?.onCollisionExit?.(rec2.guid, 0, 0, 0, 0);
          handlers2?.onCollisionExit?.(rec1.guid, 0, 0, 0, 0);
        }
      }
    });

    // Fire stay events for active pairs
    for (const key of this._activeCollisions.keys()) {
      if (currentPairs.has(key)) continue; // already handled enter this frame
      const [a, b] = key.split(':').map(Number) as [number, number];
      const rec1 = this._bodies.get(a);
      const rec2 = this._bodies.get(b);
      if (!rec1 || !rec2) continue;

      const handlers1 = this._eventHandlers.get(a);
      const handlers2 = this._eventHandlers.get(b);
      if (!handlers1?.onCollisionStay && !handlers2?.onCollisionStay) continue;

      // Get current contact data
      let cx = 0, cy = 0, nx = 0, ny = 0;
      const body1 = this._world!.getRigidBody(a);
      const body2 = this._world!.getRigidBody(b);
      if (body1 && body2) {
        // Find any collider pair in contact
        for (let i = 0; i < body1.numColliders(); i++) {
          const c1 = body1.collider(i);
          if (!c1) continue;
          for (let j = 0; j < body2.numColliders(); j++) {
            const c2 = body2.collider(j);
            if (!c2) continue;
            this._world!.contactPair(c1, c2, (manifold, _flipped) => {
              const n = manifold.normal();
              nx = n.x; ny = n.y;
              if (manifold.numSolverContacts() > 0) {
                const pt = manifold.solverContactPoint(0);
                if (pt) { cx = pt.x; cy = pt.y; }
              }
            });
          }
        }
      }

      handlers1?.onCollisionStay?.(rec2.guid, cx, cy, nx, ny);
      handlers2?.onCollisionStay?.(rec1.guid, cx, cy, -nx, -ny);
    }

    // ── Sensor/trigger events (polled) ──────────────────────────────────────
    // Rapier's collision-event queue does not reliably surface sensor intersections,
    // so we query the world directly each step and synthesize enter/stay/exit by diffing
    // against the previously-active set. We iterate every body that has an event handler
    // and probe each of its colliders (sensor OR solid) for sensor overlaps — covering
    // both "scripted trigger zone" and "scripted body rolls into a zone".
    const currentTriggers = new Set<string>();
    for (const [bh] of this._eventHandlers) {
      const body = this._world!.getRigidBody(bh as RigidBodyHandle);
      if (!body) continue;
      const recSelf = this._bodies.get(bh);
      if (!recSelf) continue;
      for (let i = 0; i < body.numColliders(); i++) {
        const col = body.collider(i);
        if (!col) continue;
        this._world!.intersectionPairsWith(col, (other) => {
          const obh = other.parent()?.handle;
          if (obh === undefined || obh === bh) return;
          const recOther = this._bodies.get(obh);
          if (!recOther) return;
          const key = bh < obh ? `${bh}:${obh}` : `${obh}:${bh}`;
          if (currentTriggers.has(key)) return; // de-dupe (found from both sides)
          currentTriggers.add(key);
          const hSelf = this._eventHandlers.get(bh);
          const hOther = this._eventHandlers.get(obh);
          if (this._activeTriggers.has(key)) {
            hSelf?.onTriggerStay?.(recOther.guid);
            hOther?.onTriggerStay?.(recSelf.guid);
          } else {
            this._activeTriggers.add(key);
            hSelf?.onTriggerEnter?.(recOther.guid);
            hOther?.onTriggerEnter?.(recSelf.guid);
          }
        });
      }
    }
    // Trigger exits — pairs active last step that no longer intersect.
    for (const key of [...this._activeTriggers]) {
      if (currentTriggers.has(key)) continue;
      this._activeTriggers.delete(key);
      const [a, b] = key.split(':').map(Number) as [number, number];
      const recA = this._bodies.get(a);
      const recB = this._bodies.get(b);
      const hA = this._eventHandlers.get(a);
      const hB = this._eventHandlers.get(b);
      if (recA && recB) {
        hA?.onTriggerExit?.(recB.guid);
        hB?.onTriggerExit?.(recA.guid);
      }
    }

    Profiler.end('Physics.Rapier.DrainEvents');
  }

  /**
   * Apply gravity zone effects to all dynamic bodies.
   * Since Rapier doesn't have native gravity zones, we apply as forces.
   */
  private _applyGravityZoneForces(_dt: number): void {
    for (const [_handle, record] of this._bodies) {
      if (record.bodyType !== PhysicsBodyType.Dynamic) continue;
      
      const body = this._world!.getRigidBody(record.handle);
      if (!body || body.isSleeping()) continue;
      
      const pos = body.translation();
      const gravityScale = body.gravityScale();
      const mass = body.mass();
      
      // Compute effective gravity from zones
      const effectiveG = this._computeEffectiveGravity(pos, this._globalGravity);
      
      // Calculate the gravity adjustment (zone effect - global)
      // Rapier already applies global gravity, so we add the difference
      const deltaGx = (effectiveG.x - this._globalGravity.x) * gravityScale;
      const deltaGy = (effectiveG.y - this._globalGravity.y) * gravityScale;
      
      if (Math.abs(deltaGx) > 0.0001 || Math.abs(deltaGy) > 0.0001) {
        // Apply as force (F = ma)
        body.applyImpulse({ x: deltaGx * mass * _dt, y: deltaGy * mass * _dt }, true);
      }
    }
  }

  /**
   * Compute effective gravity at a position considering all gravity zones.
   */
  private _computeEffectiveGravity(pos: { x: number; y: number }, globalGravity: PhysVec2): PhysVec2 {
    let gx = globalGravity.x;
    let gy = globalGravity.y;
    
    let totalWeight = 0;
    let blendGx = 0;
    let blendGy = 0;
    let hasOverride = false;
    let scaleAccum = 1;

    for (const zone of this._gravityZones.values()) {
      // Compute shape-based containment
      const containment = this._computeZoneContainment(pos.x, pos.y, zone);
      
      if (!containment.inside) continue;
      
      const falloffWeight = this._computeFalloff(containment.normalizedDist, zone);
      if (falloffWeight <= 0) continue;
      
      const weight = (zone.priority + 1) * falloffWeight;
      
      // GravityZoneType: Override=0, Additive=1, Point=2, Scale=3
      switch (zone.type as number) {
        case 0: // Override
          blendGx += zone.gravity.x * weight;
          blendGy += zone.gravity.y * weight;
          totalWeight += weight;
          hasOverride = true;
          break;
          
        case 1: // Additive
          blendGx += zone.gravity.x * falloffWeight;
          blendGy += zone.gravity.y * falloffWeight;
          break;
          
        case 2: // Point
          const strength = zone.strength * falloffWeight;
          blendGx += containment.dirX * strength;
          blendGy += containment.dirY * strength;
          break;
          
        case 3: // Scale
          scaleAccum *= 1 + (zone.strength - 1) * falloffWeight;
          break;
      }
    }

    if (hasOverride && totalWeight > 0) {
      gx = blendGx / totalWeight;
      gy = blendGy / totalWeight;
    } else {
      gx *= scaleAccum;
      gy *= scaleAccum;
    }
    
    if (!hasOverride) {
      gx += blendGx;
      gy += blendGy;
    }

    return { x: gx, y: gy };
  }

  /**
   * Transform a world point to local zone space.
   */
  private _worldToZoneLocal(wx: number, wy: number, zone: GravityZoneDescriptor): { x: number; y: number } {
    const tx = wx - zone.position.x;
    const ty = wy - zone.position.y;
    
    const cos = Math.cos(-zone.rotation);
    const sin = Math.sin(-zone.rotation);
    const rx = tx * cos - ty * sin;
    const ry = tx * sin + ty * cos;
    
    const sx = zone.scale.x !== 0 ? rx / zone.scale.x : rx;
    const sy = zone.scale.y !== 0 ? ry / zone.scale.y : ry;
    
    return { x: sx, y: sy };
  }

  /**
   * Compute zone containment info for a point.
   */
  private _computeZoneContainment(wx: number, wy: number, zone: GravityZoneDescriptor): { inside: boolean; normalizedDist: number; dirX: number; dirY: number } {
    const local = this._worldToZoneLocal(wx, wy, zone);
    
    // Direction from body to zone center (for Point gravity)
    const dx = zone.position.x - wx;
    const dy = zone.position.y - wy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const dirX = dist > 0.001 ? dx / dist : 0;
    const dirY = dist > 0.001 ? dy / dist : 0;
    
    // GravityZoneShape: Circle=0, Box=1, Capsule=2, Polygon=3
    switch (zone.shape as number) {
      case 0: { // Circle
        const r = Math.sqrt(local.x ** 2 + local.y ** 2);
        if (r > zone.outerRadius) return { inside: false, normalizedDist: 1, dirX, dirY };
        if (r <= zone.innerRadius) return { inside: true, normalizedDist: 0, dirX, dirY };
        const t = (r - zone.innerRadius) / (zone.outerRadius - zone.innerRadius);
        return { inside: true, normalizedDist: t, dirX, dirY };
      }
      
      case 1: { // Box
        const halfW = zone.size.x * 0.5;
        const halfH = zone.size.y * 0.5;
        const insideX = Math.abs(local.x) <= halfW;
        const insideY = Math.abs(local.y) <= halfH;
        if (!insideX || !insideY) return { inside: false, normalizedDist: 1, dirX, dirY };
        const maxInnerDist = Math.min(halfW, halfH);
        const distFromEdge = Math.min(halfW - Math.abs(local.x), halfH - Math.abs(local.y));
        const normalizedDist = maxInnerDist > 0 ? 1 - Math.min(distFromEdge / maxInnerDist, 1) : 0;
        return { inside: true, normalizedDist, dirX, dirY };
      }
      
      case 2: { // Capsule
        const isVertical = zone.direction === 0;
        const radius = zone.outerRadius;
        const halfLength = isVertical 
          ? Math.max(0, zone.size.y * 0.5 - radius)
          : Math.max(0, zone.size.x * 0.5 - radius);
        
        let sdf: number;
        if (isVertical) {
          const clampedY = Math.max(-halfLength, Math.min(halfLength, local.y));
          const distY = Math.abs(local.y) - halfLength;
          if (distY <= 0) {
            sdf = Math.abs(local.x) - radius;
          } else {
            sdf = Math.sqrt(distY * distY + local.x * local.x) - radius;
          }
        } else {
          const clampedX = Math.max(-halfLength, Math.min(halfLength, local.x));
          const distX = Math.abs(local.x) - halfLength;
          if (distX <= 0) {
            sdf = Math.abs(local.y) - radius;
          } else {
            sdf = Math.sqrt(distX * distX + local.y * local.y) - radius;
          }
        }
        
        if (sdf > 0) return { inside: false, normalizedDist: 1, dirX, dirY };
        const normalizedDist = radius > 0 ? 1 - Math.min(-sdf / radius, 1) : 0;
        return { inside: true, normalizedDist, dirX, dirY };
      }
      
      case 3: { // Polygon
        const points = zone.points;
        if (!points || points.length < 3) return { inside: false, normalizedDist: 1, dirX, dirY };
        
        // Ray casting for point-in-polygon
        let inside = false;
        let minEdgeDist = Infinity;
        
        for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
          const xi = points[i].x, yi = points[i].y;
          const xj = points[j].x, yj = points[j].y;
          
          if (((yi > local.y) !== (yj > local.y)) && 
              (local.x < (xj - xi) * (local.y - yi) / (yj - yi) + xi)) {
            inside = !inside;
          }
          
          // Distance to edge
          const edgeX = xj - xi, edgeY = yj - yi;
          const edgeLen = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
          if (edgeLen > 0) {
            const t = Math.max(0, Math.min(1, ((local.x - xi) * edgeX + (local.y - yi) * edgeY) / (edgeLen * edgeLen)));
            const closestX = xi + t * edgeX;
            const closestY = yi + t * edgeY;
            const d = Math.sqrt((local.x - closestX) ** 2 + (local.y - closestY) ** 2);
            minEdgeDist = Math.min(minEdgeDist, d);
          }
        }
        
        if (!inside) return { inside: false, normalizedDist: 1, dirX, dirY };
        
        // Compute centroid for normalized distance
        let cx = 0, cy = 0;
        for (const p of points) { cx += p.x; cy += p.y; }
        cx /= points.length;
        cy /= points.length;
        
        let maxRadius = 0;
        for (const p of points) {
          const d = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
          maxRadius = Math.max(maxRadius, d);
        }
        
        const distToCenter = Math.sqrt((local.x - cx) ** 2 + (local.y - cy) ** 2);
        const normalizedDist = maxRadius > 0 ? Math.min(distToCenter / maxRadius, 1) : 0;
        
        return { inside: true, normalizedDist, dirX, dirY };
      }
      
      default:
        return { inside: false, normalizedDist: 1, dirX, dirY };
    }
  }

  /**
   * Compute falloff weight based on normalized distance and zone config.
   */
  private _computeFalloff(normalizedDist: number, zone: GravityZoneDescriptor): number {
    const { falloff } = zone;
    
    if (normalizedDist <= 0) return 1;
    if (normalizedDist >= 1) return 0;
    
    const t = normalizedDist;
    
    // GravityFalloff: None=0, Linear=1, InverseSquare=2, Smoothstep=3
    switch (falloff as number) {
      case 0: // None
        return 1;
      case 1: // Linear
        return 1 - t;
      case 2: // InverseSquare
        return 1 / (1 + t * t * 3);
      case 3: // Smoothstep
        return 1 - (t * t * (3 - 2 * t));
      default:
        return 1 - t;
    }
  }

  /**
   * Initialize physics hooks for contact filtering (one-way platforms).
   * Called after world creation.
   */
  private _initPhysicsHooks(): void {
    this._physicsHooks = {
      filterContactPair: (
        collider1Handle: ColliderHandle,
        collider2Handle: ColliderHandle,
        body1Handle: RigidBodyHandle,
        body2Handle: RigidBodyHandle,
      ): SolverFlags | null => {
        // Check if either collider is a one-way platform
        const oneWay1 = this._oneWayColliders.get(collider1Handle);
        const oneWay2 = this._oneWayColliders.get(collider2Handle);
        
        if (!oneWay1 && !oneWay2) {
          // Neither collider is one-way, allow normal contact
          return null;
        }

        // Get the one-way direction and the "other" body
        const oneWayDir = oneWay1 || oneWay2!;
        const platformBodyHandle = oneWay1 ? body1Handle : body2Handle;
        const otherBodyHandle = oneWay1 ? body2Handle : body1Handle;

        // Get bodies
        const platformBody = this._world!.getRigidBody(platformBodyHandle);
        const otherBody = this._world!.getRigidBody(otherBodyHandle);
        if (!platformBody || !otherBody) return null;

        // Get positions and velocity of the other body
        const platformPos = platformBody.translation();
        const otherPos = otherBody.translation();
        const otherVel = otherBody.linvel();

        // Calculate relative position (from platform to other body)
        const relPosX = otherPos.x - platformPos.x;
        const relPosY = otherPos.y - platformPos.y;

        // Dot product of relative position with one-way direction
        // If positive, other body is on the "solid" side
        // If negative, other body is on the "passable" side
        const posDot = relPosX * oneWayDir.x + relPosY * oneWayDir.y;

        // Dot product of velocity with one-way direction
        // If negative, other body is moving toward the solid side
        const velDot = otherVel.x * oneWayDir.x + otherVel.y * oneWayDir.y;

        // Allow contact if:
        // 1. Body is on the solid side (posDot > threshold), OR
        // 2. Body is moving into the platform from solid side (velDot < 0 and on solid side)
        //
        // Filter contact (disable collision) if:
        // - Body is on the passable side (posDot < 0) and moving toward platform
        const threshold = 0.01; // Small threshold to handle edge cases
        
        if (posDot < threshold && velDot < 0) {
          // Body is on passable side and moving toward platform - allow passage
          return SolverFlags.EMPTY;
        }

        // Normal collision
        return null;
      },
      
      // Filter intersection pairs (for triggers/sensors) - allow all
      filterIntersectionPair: (
        _collider1Handle: ColliderHandle,
        _collider2Handle: ColliderHandle,
      ): boolean => {
        // Return true to allow intersection detection
        return true;
      },
    };
  }

  // ──────── Events ────────

  setEventHandler(handle: PhysicsBodyHandle, handlers: PhysicsBodyEventHandlers): void {
    const h = handle as RigidBodyHandle;
    this._eventHandlers.set(h, handlers);
    console.warn(`[RapierPhysicsEngine] setEventHandler is experimental and may have performance implications. Use with simple handlers and avoid heavy computations.`);
    // Enable collision events on all colliders of this body so Rapier reports them
    if (this._world) {
      const body = this._world.getRigidBody(h);
      if (body) {
        for (let i = 0; i < body.numColliders(); i++) {
          const col = body.collider(i);
          if (col) col.setActiveEvents(ActiveEvents.COLLISION_EVENTS);
        }
      }
    }
  }

  // ──────── Read-back ────────

  forEachBody(fn: (body: BodyTransform) => void): void {
    if (!this._world) return;

    this._world.forEachRigidBody((rapierBody: RigidBody) => {
      const record = this._bodies.get(rapierBody.handle);
      if (!record) return;

      // Skip static bodies (they don't move)
      if (rapierBody.isFixed()) return;

      const pos = rapierBody.translation();
      const rot = rapierBody.rotation();

      fn({
        guid: record.guid,
        gameObjectId: record.gameObjectId,
        bodyType: fromRapierBodyType(rapierBody.bodyType()),
        position: { x: pos.x, y: pos.y },
        rotation: rot,
      });
    });
  }

  // ──────── Forces ────────

  applyForce(handle: PhysicsBodyHandle, force: PhysVec2): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.addForce(force, true);
  }

  applyImpulse(handle: PhysicsBodyHandle, impulse: PhysVec2): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.applyImpulse(impulse, true);
  }

  applyTorque(handle: PhysicsBodyHandle, torque: number): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.addTorque(torque, true);
  }

  applyAngularImpulse(handle: PhysicsBodyHandle, impulse: number): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.applyTorqueImpulse(impulse, true);
  }

  // ──────── Velocity ────────

  getLinearVelocity(handle: PhysicsBodyHandle): PhysVec2 {
    if (!this._world) return { x: 0, y: 0 };
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (!body) return { x: 0, y: 0 };
    const vel = body.linvel();
    return { x: vel.x, y: vel.y };
  }

  setLinearVelocity(handle: PhysicsBodyHandle, velocity: PhysVec2): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.setLinvel(velocity, true);
  }

  getAngularVelocity(handle: PhysicsBodyHandle): number {
    if (!this._world) return 0;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    return body?.angvel() ?? 0;
  }

  setAngularVelocity(handle: PhysicsBodyHandle, velocity: number): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.setAngvel(velocity, true);
  }

  // ──────── Body Properties ────────

  setLinearDamping(handle: PhysicsBodyHandle, damping: number): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.setLinearDamping(damping);
  }

  setAngularDamping(handle: PhysicsBodyHandle, damping: number): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.setAngularDamping(damping);
  }

  setGravityScale(handle: PhysicsBodyHandle, scale: number): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.setGravityScale(scale, true);
  }

  getMass(handle: PhysicsBodyHandle): number {
    if (!this._world) return 0;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    return body?.mass() ?? 0;
  }

  setMass(handle: PhysicsBodyHandle, mass: number): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) {
      // Rapier doesn't have setMass directly, we need to use additional mass properties
      body.setAdditionalMass(mass - body.mass(), true);
    }
  }

  // ──────── Sleep ────────

  sleep(handle: PhysicsBodyHandle): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.sleep();
  }

  wake(handle: PhysicsBodyHandle): void {
    if (!this._world) return;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    if (body) body.wakeUp();
  }

  isSleeping(handle: PhysicsBodyHandle): boolean {
    if (!this._world) return false;
    const body = this._world.getRigidBody(handle as RigidBodyHandle);
    return body?.isSleeping() ?? false;
  }

  // ──────── Overlap Queries ────────

  overlapCircle(center: PhysVec2, radius: number, _layerMask?: number): PhysicsBodyHandle[] {
    if (!this._world) return [];
    const results: PhysicsBodyHandle[] = [];
    const shape = new RAPIER.Ball(radius);
    this._world.intersectionsWithShape(center, 0, shape, (collider) => {
      const body = collider.parent();
      if (body) results.push(body.handle);
      return true;
    });
    return results;
  }

  overlapBox(center: PhysVec2, halfExtents: PhysVec2, _layerMask?: number): PhysicsBodyHandle[] {
    if (!this._world) return [];
    const results: PhysicsBodyHandle[] = [];
    const shape = new RAPIER.Cuboid(halfExtents.x, halfExtents.y);
    this._world.intersectionsWithShape(center, 0, shape, (collider) => {
      const body = collider.parent();
      if (body) results.push(body.handle);
      return true;
    });
    return results;
  }

  overlapPoint(point: PhysVec2, _layerMask?: number): PhysicsBodyHandle[] {
    if (!this._world) return [];
    const results: PhysicsBodyHandle[] = [];
    this._world.intersectionsWithPoint(point, (collider) => {
      const body = collider.parent();
      if (body) results.push(body.handle);
      return true;
    });
    return results;
  }

  // ──────── Queries ────────

  raycast(origin: PhysVec2, direction: PhysVec2, maxDistance: number, _layerMask?: number): RaycastHit | null {
    if (!this._world) return null;

    const ray = new RAPIER.Ray(origin, direction);
    const hit = this._world.castRayAndGetNormal(ray, maxDistance, true);

    if (!hit) return null;

    const hitPoint = ray.pointAt(hit.timeOfImpact);

    // Find the body handle from the collider
    const collider = this._world.getCollider(hit.collider.handle);
    const parentBody = collider?.parent();
    const bodyHandle = parentBody?.handle ?? 0;

    return {
      point: { x: hitPoint.x, y: hitPoint.y },
      normal: { x: hit.normal.x, y: hit.normal.y },
      distance: hit.timeOfImpact,
      bodyHandle,
    };
  }

  raycastAll(origin: PhysVec2, direction: PhysVec2, maxDistance: number, _layerMask?: number): RaycastHit[] {
    if (!this._world) return [];

    const results: RaycastHit[] = [];
    const ray = new RAPIER.Ray(origin, direction);

    this._world.intersectionsWithRay(ray, maxDistance, true, (hit) => {
      const hitPoint = ray.pointAt(hit.timeOfImpact);
      const hitNormal = hit.normal;
      const collider = this._world!.getCollider(hit.collider.handle);
      const parentBody = collider?.parent();

      results.push({
        point: { x: hitPoint.x, y: hitPoint.y },
        normal: { x: hitNormal.x, y: hitNormal.y },
        distance: hit.timeOfImpact,
        bodyHandle: parentBody?.handle ?? 0,
      });
      return true; // continue iteration
    });

    return results;
  }

  // ──────── Gravity Zones ────────

  addGravityZone(desc: GravityZoneDescriptor): void {
    this._gravityZones.set(desc.guid, {
      ...desc,
      position: desc.position ? { ...desc.position } : { x: 0, y: 0 },
      rotation: desc.rotation ?? 0,
      scale: desc.scale ? { ...desc.scale } : { x: 1, y: 1 },
      shape: desc.shape ?? 0,
      gravity: desc.gravity ? { ...desc.gravity } : { x: 0, y: 0 },
      size: desc.size ? { ...desc.size } : { x: 4, y: 2 },
      points: desc.points ? desc.points.map(p => ({ ...p })) : [],
    });
  }

  updateGravityZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    const zone = this._gravityZones.get(guid);
    if (zone) {
      zone.position.x = position.x;
      zone.position.y = position.y;
      if (rotation !== undefined) zone.rotation = rotation;
      if (scale !== undefined) {
        zone.scale.x = scale.x;
        zone.scale.y = scale.y;
      }
    }
  }

  updateGravityZone(guid: string, desc: Partial<GravityZoneDescriptor>): void {
    const zone = this._gravityZones.get(guid);
    if (zone) {
      if (desc.type !== undefined) zone.type = desc.type;
      if (desc.shape !== undefined) zone.shape = desc.shape;
      if (desc.gravity) {
        zone.gravity.x = desc.gravity.x;
        zone.gravity.y = desc.gravity.y;
      }
      if (desc.strength !== undefined) zone.strength = desc.strength;
      if (desc.falloff !== undefined) zone.falloff = desc.falloff;
      if (desc.innerRadius !== undefined) zone.innerRadius = desc.innerRadius;
      if (desc.outerRadius !== undefined) zone.outerRadius = desc.outerRadius;
      if (desc.size) {
        zone.size.x = desc.size.x;
        zone.size.y = desc.size.y;
      }
      if (desc.direction !== undefined) zone.direction = desc.direction;
      if (desc.points) zone.points = desc.points.map(p => ({ ...p }));
      if (desc.priority !== undefined) zone.priority = desc.priority;
      if (desc.affectedLayers !== undefined) zone.affectedLayers = desc.affectedLayers;
    }
  }

  removeGravityZone(guid: string): void {
    this._gravityZones.delete(guid);
  }

  // ──────── Buoyancy Zones ────────

  addBuoyancyZone(desc: BuoyancyZoneDescriptor): void {
    this._buoyancyZones.set(desc.guid, {
      ...desc,
      position: desc.position ? { ...desc.position } : { x: 0, y: 0 },
      rotation: desc.rotation ?? 0,
      scale: desc.scale ? { ...desc.scale } : { x: 1, y: 1 },
      flow: desc.flow ? { ...desc.flow } : { x: 0, y: 0 },
      surfaceNormal: desc.surfaceNormal ? { ...desc.surfaceNormal } : { x: 0, y: 1 },
    });
  }

  updateBuoyancyZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    const zone = this._buoyancyZones.get(guid);
    if (zone) {
      zone.position.x = position.x;
      zone.position.y = position.y;
      if (rotation !== undefined) zone.rotation = rotation;
      if (scale !== undefined) {
        zone.scale.x = scale.x;
        zone.scale.y = scale.y;
      }
    }
  }

  updateBuoyancyZone(guid: string, desc: Partial<BuoyancyZoneDescriptor>): void {
    const zone = this._buoyancyZones.get(guid);
    if (zone) {
      if (desc.density !== undefined) zone.density = desc.density;
      if (desc.linearDrag !== undefined) zone.linearDrag = desc.linearDrag;
      if (desc.angularDrag !== undefined) zone.angularDrag = desc.angularDrag;
      if (desc.flow) {
        zone.flow.x = desc.flow.x;
        zone.flow.y = desc.flow.y;
      }
      if (desc.surfaceNormal) {
        zone.surfaceNormal.x = desc.surfaceNormal.x;
        zone.surfaceNormal.y = desc.surfaceNormal.y;
      }
      if (desc.width !== undefined) zone.width = desc.width;
      if (desc.height !== undefined) zone.height = desc.height;
      if (desc.priority !== undefined) zone.priority = desc.priority;
      if (desc.affectedLayers !== undefined) zone.affectedLayers = desc.affectedLayers;
    }
  }

  removeBuoyancyZone(guid: string): void {
    this._buoyancyZones.delete(guid);
  }

  // ──────── Wind Zones ────────

  addWindZone(desc: WindZoneDescriptor): void {
    this._windZones.set(desc.guid, {
      ...desc,
      position: desc.position ? { ...desc.position } : { x: 0, y: 0 },
      rotation: desc.rotation ?? 0,
      scale: desc.scale ? { ...desc.scale } : { x: 1, y: 1 },
      direction: desc.direction ? { ...desc.direction } : { x: 1, y: 0 },
    });
  }

  updateWindZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    const zone = this._windZones.get(guid);
    if (zone) {
      zone.position.x = position.x;
      zone.position.y = position.y;
      if (rotation !== undefined) zone.rotation = rotation;
      if (scale !== undefined) {
        zone.scale.x = scale.x;
        zone.scale.y = scale.y;
      }
    }
  }

  updateWindZone(guid: string, desc: Partial<WindZoneDescriptor>): void {
    const zone = this._windZones.get(guid);
    if (zone) {
      if (desc.mode !== undefined) zone.mode = desc.mode;
      if (desc.direction) {
        zone.direction.x = desc.direction.x;
        zone.direction.y = desc.direction.y;
      }
      if (desc.strength !== undefined) zone.strength = desc.strength;
      if (desc.turbulence !== undefined) zone.turbulence = desc.turbulence;
      if (desc.turbulenceFrequency !== undefined) zone.turbulenceFrequency = desc.turbulenceFrequency;
      if (desc.pulse !== undefined) zone.pulse = desc.pulse;
      if (desc.pulseFrequency !== undefined) zone.pulseFrequency = desc.pulseFrequency;
      if (desc.shape !== undefined) zone.shape = desc.shape;
      if (desc.radius !== undefined) zone.radius = desc.radius;
      if (desc.width !== undefined) zone.width = desc.width;
      if (desc.height !== undefined) zone.height = desc.height;
      if (desc.falloff !== undefined) zone.falloff = desc.falloff;
      if (desc.falloffStart !== undefined) zone.falloffStart = desc.falloffStart;
      if (desc.priority !== undefined) zone.priority = desc.priority;
      if (desc.affectedLayers !== undefined) zone.affectedLayers = desc.affectedLayers;
    }
  }

  removeWindZone(guid: string): void {
    this._windZones.delete(guid);
  }

  // ──────── Surface Effectors ────────

  addSurfaceEffector(desc: SurfaceEffectorDescriptor): void {
    // Surface effectors are handled via contact callbacks
    // TODO: Implement surface effector logic
  }

  updateSurfaceEffector(guid: string, desc: Partial<SurfaceEffectorDescriptor>): void {
    // TODO: Implement
  }

  removeSurfaceEffector(guid: string): void {
    // TODO: Implement
  }

  // ──────── Effector Force Application ────────

  /**
   * Apply buoyancy and wind forces to dynamic bodies.
   */
  private _applyEffectorForces(dt: number): void {
    for (const [_handle, record] of this._bodies) {
      if (record.bodyType !== PhysicsBodyType.Dynamic) continue;

      const body = this._world!.getRigidBody(record.handle);
      if (!body || body.isSleeping()) continue;

      const pos = body.translation();
      const mass = body.mass();

      // Apply buoyancy
      for (const zone of this._buoyancyZones.values()) {
        const inside = this._isInsideBuoyancyZone(pos.x, pos.y, zone);
        if (!inside.inside) continue;

        const submergedFrac = inside.depth;

        // Buoyancy force using Archimedes' principle:
        // F_buoy = (ρ_fluid / ρ_body) * mass * g * submergedFrac
        const normalMag = Math.sqrt(zone.surfaceNormal.x ** 2 + zone.surfaceNormal.y ** 2);
        const nx = normalMag > 0.001 ? zone.surfaceNormal.x / normalMag : 0;
        const ny = normalMag > 0.001 ? zone.surfaceNormal.y / normalMag : 1;
        const bodyDensity = record.density > 0.001 ? record.density : 1;
        const buoyancyForce = (zone.density / bodyDensity) * mass * 9.81 * submergedFrac;

        // Apply buoyancy + flow as impulse
        const fx = (nx * buoyancyForce + zone.flow.x * submergedFrac * mass) * dt;
        const fy = (ny * buoyancyForce + zone.flow.y * submergedFrac * mass) * dt;
        body.applyImpulse({ x: fx, y: fy }, true);

        // Apply drag
        const linVel = body.linvel();
        const angVel = body.angvel();
        const dragFactor = Math.max(0, 1 - zone.linearDrag * dt * submergedFrac);
        const angDragFactor = Math.max(0, 1 - zone.angularDrag * dt * submergedFrac);
        body.setLinvel({ x: linVel.x * dragFactor, y: linVel.y * dragFactor }, true);
        body.setAngvel(angVel * angDragFactor, true);
      }

      // Apply wind
      for (const zone of this._windZones.values()) {
        const inside = this._isInsideWindZone(pos.x, pos.y, zone);
        if (!inside.inside) continue;

        let strength = zone.strength * inside.falloffFactor;

        // Turbulence
        if (zone.turbulence > 0) {
          const turbNoise = Math.sin(this._time * zone.turbulenceFrequency * 6.28 + pos.x * 0.5) *
                            Math.cos(this._time * zone.turbulenceFrequency * 4.17 + pos.y * 0.7);
          strength *= 1 + zone.turbulence * turbNoise;
        }

        // Pulse
        if (zone.pulse > 0) {
          const pulseFactor = 1 + zone.pulse * Math.sin(this._time * zone.pulseFrequency * 6.28);
          strength *= pulseFactor;
        }

        // Wind direction
        let wx: number, wy: number;
        if (zone.mode === 0) {
          // Directional
          const dirMag = Math.sqrt(zone.direction.x ** 2 + zone.direction.y ** 2);
          if (dirMag < 0.001) continue;
          wx = zone.direction.x / dirMag;
          wy = zone.direction.y / dirMag;
        } else {
          // Radial
          const dx = pos.x - zone.position.x;
          const dy = pos.y - zone.position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 0.001) continue;
          wx = dx / dist;
          wy = dy / dist;
        }

        // Apply wind impulse
        body.applyImpulse({ x: wx * strength * dt, y: wy * strength * dt }, true);
      }
    }
  }

  /**
   * Check if point is inside buoyancy zone.
   */
  private _isInsideBuoyancyZone(x: number, y: number, zone: BuoyancyZoneDescriptor): { inside: boolean; depth: number } {
    const tx = x - zone.position.x;
    const ty = y - zone.position.y;

    const cos = Math.cos(-zone.rotation);
    const sin = Math.sin(-zone.rotation);
    const rx = tx * cos - ty * sin;
    const ry = tx * sin + ty * cos;

    const sx = zone.scale.x;
    const sy = zone.scale.y;
    const lx = sx !== 0 ? rx / sx : rx;
    const ly = sy !== 0 ? ry / sy : ry;

    const halfW = zone.width * 0.5;
    const halfH = zone.height * 0.5;

    if (Math.abs(lx) > halfW || Math.abs(ly) > halfH) {
      return { inside: false, depth: 0 };
    }

    const normalMag = Math.sqrt(zone.surfaceNormal.x ** 2 + zone.surfaceNormal.y ** 2);
    const nx = normalMag > 0.001 ? zone.surfaceNormal.x / normalMag : 0;
    const ny = normalMag > 0.001 ? zone.surfaceNormal.y / normalMag : 1;
    const projectedY = -(lx * nx + ly * ny);
    const depth = Math.max(0, Math.min(1, (projectedY + halfH) / zone.height));

    return { inside: true, depth };
  }

  /**
   * Check if point is inside wind zone.
   */
  private _isInsideWindZone(x: number, y: number, zone: WindZoneDescriptor): { inside: boolean; falloffFactor: number } {
    const tx = x - zone.position.x;
    const ty = y - zone.position.y;

    const cos = Math.cos(-zone.rotation);
    const sin = Math.sin(-zone.rotation);
    const rx = tx * cos - ty * sin;
    const ry = tx * sin + ty * cos;

    const sx = zone.scale.x;
    const sy = zone.scale.y;
    const lx = sx !== 0 ? rx / sx : rx;
    const ly = sy !== 0 ? ry / sy : ry;

    let inside = false;
    let normalizedDist = 0;

    if (zone.shape === 0) {
      const r = Math.sqrt(lx * lx + ly * ly);
      inside = r <= zone.radius;
      normalizedDist = zone.radius > 0 ? r / zone.radius : 0;
    } else {
      const halfW = zone.width * 0.5;
      const halfH = zone.height * 0.5;
      inside = Math.abs(lx) <= halfW && Math.abs(ly) <= halfH;
      if (inside) {
        const fx = halfW > 0 ? Math.abs(lx) / halfW : 0;
        const fy = halfH > 0 ? Math.abs(ly) / halfH : 0;
        normalizedDist = Math.max(fx, fy);
      }
    }

    if (!inside) return { inside: false, falloffFactor: 0 };

    let falloffFactor = 1;
    if (zone.falloff > 0 && normalizedDist > zone.falloffStart) {
      const t = (normalizedDist - zone.falloffStart) / (1 - zone.falloffStart);
      if (zone.falloff === 1) {
        falloffFactor = 1 - t;
      } else {
        falloffFactor = 1 - (t * t * (3 - 2 * t));
      }
    }

    return { inside: true, falloffFactor };
  }

  // ──────── Helpers ────────

  private _assertWorld(): void {
    if (!this._world) throw new Error('[RapierPhysicsEngine] No world created. Call createWorld() first.');
  }

  // ──────── Diagnostics ────────

  getDiagnostics(): PhysicsDiagnostics {
    // Count bodies by state
    let activeBodies = 0;
    let sleepingBodies = 0;
    let colliderCount = 0;

    if (this._world) {
      this._world.forEachRigidBody((body) => {
        if (body.isSleeping()) {
          sleepingBodies++;
        } else {
          activeBodies++;
        }
      });

      this._world.forEachCollider(() => {
        colliderCount++;
      });
    }

    const bodyCount = activeBodies + sleepingBodies;

    return {
      bodyCount,
      colliderCount,
      activeBodies,
      sleepingBodies,
      broadPhasePairs: 0, // Not directly exposed by Rapier
      narrowPhaseContacts: 0, // Would need custom tracking
      islandCount: 0, // Not directly accessible in Rapier
      stepCount: 0, // Could be tracked if needed
      stepTimeMs: 0, // Would need timestamp tracking
    };
  }
}
